import { useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Plus } from "lucide-react";
import { apiRequest } from "@/lib/queryClient";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";

type Chip = "today" | "tomorrow" | "next_mon";

function dateFor(chip: Chip): string {
  const d = new Date();
  d.setHours(17, 0, 0, 0);
  if (chip === "tomorrow") d.setDate(d.getDate() + 1);
  if (chip === "next_mon") {
    const dow = d.getDay();
    const offset = dow === 1 ? 7 : (8 - dow) % 7 || 7;
    d.setDate(d.getDate() + offset);
  }
  return d.toISOString();
}

export function QuickAddTask() {
  const [expanded, setExpanded] = useState(false);
  const [title, setTitle] = useState("");
  const [chip, setChip] = useState<Chip>("today");
  const qc = useQueryClient();

  const create = useMutation({
    mutationFn: () =>
      apiRequest("POST", "/api/tasks", {
        title: title.trim(),
        dueDate: dateFor(chip),
        priority: "medium",
        status: "pending",
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["/api/tasks"] });
      qc.invalidateQueries({ queryKey: ["dashboard", "radar"] });
      toast.success("Task added");
      setTitle("");
    },
    onError: () => toast.error("Couldn't add task"),
  });

  const submit = () => {
    if (!title.trim()) return;
    create.mutate();
  };

  return (
    <div className="px-1 pb-2">
      {!expanded ? (
        <button
          onClick={() => setExpanded(true)}
          className="flex items-center gap-2 w-full rounded-lg border border-dashed border-border bg-card hover:bg-accent px-3 py-2 text-sm text-muted-foreground"
        >
          <Plus className="h-4 w-4" /> Add a task
        </button>
      ) : (
        <div className="rounded-lg border border-border bg-card p-2 space-y-2">
          <Input
            autoFocus
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder="Task title"
            onKeyDown={(e) => {
              if (e.key === "Enter") submit();
              if (e.key === "Escape") {
                setTitle("");
                setExpanded(false);
              }
            }}
            className="h-8 text-sm"
          />
          <div className="flex items-center gap-1 flex-wrap">
            {(["today", "tomorrow", "next_mon"] as Chip[]).map((c) => (
              <button
                key={c}
                onClick={() => setChip(c)}
                className={`text-[11px] px-2 py-0.5 rounded-full border ${
                  chip === c
                    ? "border-primary bg-primary/10 text-primary-text"
                    : "border-border text-muted-foreground"
                }`}
              >
                {c === "today" ? "Today" : c === "tomorrow" ? "Tomorrow" : "Next Mon"}
              </button>
            ))}
            <span className="flex-1" />
            <Button
              size="sm"
              onClick={submit}
              disabled={create.isPending || !title.trim()}
              className="h-7 text-xs"
            >
              Add
            </Button>
            <Button
              size="sm"
              variant="ghost"
              onClick={() => {
                setTitle("");
                setExpanded(false);
              }}
              className="h-7 text-xs"
            >
              Cancel
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
